import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { colors, fonts, layout, spacing } from './theme';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from './lib/supabase';
import PortalHeader from './PortalHeader';
import PortalFooter from './PortalFooter';
import LoadingSpinner from './components/LoadingSpinner';
import SkeletonLoader from './components/SkeletonLoader';
import EmptyState from './components/EmptyState';
import Button from './components/Button';
import Input from './components/Input';
import Card from './components/Card';
import Table from './components/Table';
import Modal from './components/Modal';
import FileUploadZone from './components/FileUploadZone';
import Icon from './components/Icon';
import SearchBar from './components/SearchBar';
import AdminNav from './components/AdminNav';
import MatchingPortal from './admin/MatchingPortal';
import { fetchUnmatchedCount } from './admin/hooks/useIncomingRecords';
import EntityTypeSelect from './components/EntityTypeSelect';
import { retryOperation, sanitizeFilename, sanitizeInput } from './utils/helpers';
import { showActionableError } from './utils/errorMessages';
import { useKeyboardShortcuts } from './utils/useKeyboardShortcuts';
import { createUndoableAction } from './utils/undo';
import { auditProfileChanges } from './utils/auditProfileChanges';
import { DOCUMENT_TYPES } from './constants/documents';
import { insertFilingWithFallback } from './utils/insertFiling';
import { validateBusinessName } from './utils/validation';


const styles = {
  container: {
    maxWidth: '100%',
    width: '100%',
    margin: '0 auto',
    padding: layout.pagePadding,
    fontFamily: fonts.body,
    minHeight: '100vh',
    backgroundColor: colors.background,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  content: {
    flex: 1,
    minWidth: 0,
    width: '100%',
  },
  header: {
    backgroundColor: colors.white,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderRadius: layout.borderRadius,
    boxShadow: layout.boxShadow,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  badge: {
    fontFamily: fonts.heading,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    backgroundColor: colors.darkBlue,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '999px',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 3fr)',
    gap: spacing.lg,
    alignItems: 'start',
    minWidth: 0,
  },
  pane: {
    minWidth: 0,
    maxWidth: '100%',
  },
  paneScroll: {
    minWidth: 0,
  },
  select: {
    fontFamily: fonts.body,
    fontSize: '1rem',
    height: '2.25rem',
    padding: '0.5rem',
    border: `1px solid ${colors.borderColor}`,
    borderRadius: layout.borderRadius,
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: colors.white,
    transition: 'all 0.2s ease',
  },
  statusPill: {
    display: 'inline-block',
    fontSize: '0.75rem',
    fontFamily: fonts.heading,
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '999px',
    marginRight: '0.25rem',
    marginBottom: '0.15rem',
  },
};

function Admin() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingUser, setLoadingUser] = useState(true);

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);

  const [clientFilings, setClientFilings] = useState([]);
  const [filingsLoading, setFilingsLoading] = useState(false);
  const [filingsError, setFilingsError] = useState('');

  const [businessName, setBusinessName] = useState('');
  const [entityType, setEntityType] = useState('');
  const [savingClient, setSavingClient] = useState(false);

  // Owner information
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [ownerAddressLine1, setOwnerAddressLine1] = useState('');
  const [ownerAddressLine2, setOwnerAddressLine2] = useState('');
  const [ownerCity, setOwnerCity] = useState('');
  const [ownerState, setOwnerState] = useState('');
  const [ownerZip, setOwnerZip] = useState('');

  // Business information
  const [businessAddressLine1, setBusinessAddressLine1] = useState('');
  const [businessAddressLine2, setBusinessAddressLine2] = useState('');
  const [businessCity, setBusinessCity] = useState('');
  const [businessState, setBusinessState] = useState('');
  const [businessZip, setBusinessZip] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [ein, setEin] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');

  // Registered agent information
  const [registeredAgentName, setRegisteredAgentName] = useState('');
  const [registeredAgentAddressLine1, setRegisteredAgentAddressLine1] = useState('');
  const [registeredAgentAddressLine2, setRegisteredAgentAddressLine2] = useState('');
  const [registeredAgentCity, setRegisteredAgentCity] = useState('');
  const [registeredAgentState, setRegisteredAgentState] = useState('');
  const [registeredAgentZip, setRegisteredAgentZip] = useState('');
  const [registeredAgentPhone, setRegisteredAgentPhone] = useState('');
  const [registeredAgentEmail, setRegisteredAgentEmail] = useState('');

  const [newTitle, setNewTitle] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [tag, setTag] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadZoneKey, setUploadZoneKey] = useState(0);
  const [clientFolders, setClientFolders] = useState([]);
  const [syncingNWRA, setSyncingNWRA] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [filingToDelete, setFilingToDelete] = useState(null);

  // Pending updates from JotForm
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [pendingUpdatesLoading, setPendingUpdatesLoading] = useState(false);
  const [selectedPendingUpdate, setSelectedPendingUpdate] = useState(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [reviewingUpdate, setReviewingUpdate] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isManuallyRefreshing, setIsManuallyRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // Clients list search/filter (separate from pending updates)
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientFilterEntityType, setClientFilterEntityType] = useState('all');
  
  // NWRA submission
  const [nwraOrders, setNwraOrders] = useState([]);
  const [submittingToNWRA, setSubmittingToNWRA] = useState(false);
  const [retryingOrderId, setRetryingOrderId] = useState(null);
  const [markingDirectFiling, setMarkingDirectFiling] = useState(false);
  const [profileChanges, setProfileChanges] = useState(null);
  const [profileChangesLoading, setProfileChangesLoading] = useState(false);
  const [profileChangesError, setProfileChangesError] = useState('');
  const [testingNWRA, setTestingNWRA] = useState(false);
  const [nwraTestResults, setNwraTestResults] = useState(null);
  const [unmatchedIncomingCount, setUnmatchedIncomingCount] = useState(0);
  
  // Client detail tabs
  const [activeClientTab, setActiveClientTab] = useState('information');

  // NWRA company search / link
  const [nwraSearchName, setNwraSearchName] = useState('');
  const [nwraSearchResults, setNwraSearchResults] = useState([]);
  const [nwraSearching, setNwraSearching] = useState(false);
  const [syncingNWRACompany, setSyncingNWRACompany] = useState(false);

  // Stripe customer IDs (admin override)
  const [stripeCustomerDirect, setStripeCustomerDirect] = useState('');
  const [stripeCustomerJotform, setStripeCustomerJotform] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [linkingPortalUser, setLinkingPortalUser] = useState(false);
  const [billingPreview, setBillingPreview] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const routeClientId = (() => {
    const m = location.pathname.match(/\/admin\/clients\/([^/]+)/);
    return m ? m[1] : null;
  })();

  useEffect(() => {
    if (!routeClientId || !clients.length) return;
    const match = clients.find((c) => String(c.id) === String(routeClientId));
    if (match && selectedClient?.id !== match.id) {
      selectClient(match, { navigateToDetail: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeClientId, clients]);

  useEffect(() => {
    const init = async () => {
      const { data } = await supabase.auth.getUser();
      const currentUser = data?.user || null;
      setUser(currentUser);

      const admin = currentUser && currentUser.user_metadata && currentUser.user_metadata.role === 'admin';
      setIsAdmin(!!admin);
      setLoadingUser(false);

      if (admin) {
        loadClients();
        loadPendingUpdates();
        loadNWRAOrders();
      }
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh pending updates every 30 seconds
  useEffect(() => {
    if (!isAdmin || isManuallyRefreshing) return;

    // Initial load
    loadPendingUpdates();

    // Set up polling
    const interval = setInterval(() => {
      loadPendingUpdates();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isManuallyRefreshing]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchUnmatchedCount().then(setUnmatchedIncomingCount);
    const interval = setInterval(() => {
      fetchUnmatchedCount().then(setUnmatchedIncomingCount);
    }, 30000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Load JotForm embed handler script when service form tab is active
  useEffect(() => {
    if (activeClientTab !== 'serviceForm') return;

    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js"]');
    
    if (existingScript) {
      // Script already loaded, just initialize once
      if (window.jotformEmbedHandler) {
        // Use requestAnimationFrame to ensure iframe is fully rendered
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.jotformEmbedHandler("iframe[id='JotFormIFrame-252943654936065']", "https://form.jotform.com/");
          }, 500);
        });
      }
      return;
    }

    // Load the script only once
    const script = document.createElement('script');
    script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
    script.async = true;
    script.onload = () => {
      if (window.jotformEmbedHandler) {
        // Use requestAnimationFrame to ensure iframe is fully rendered
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.jotformEmbedHandler("iframe[id='JotFormIFrame-252943654936065']", "https://form.jotform.com/");
          }, 500);
        });
      }
    };
    document.body.appendChild(script);
  }, [activeClientTab]);

  // Keyboard shortcuts
  useKeyboardShortcuts(
    {
      'escape': () => {
        if (showPendingModal) {
          setShowPendingModal(false);
          setSelectedPendingUpdate(null);
        }
      },
      'a': () => {
        if (showPendingModal && selectedPendingUpdate && !reviewingUpdate) {
          approvePendingUpdate(selectedPendingUpdate);
        }
      },
      'r': () => {
        if (showPendingModal && selectedPendingUpdate && isAdmin) {
          const reason = prompt('Rejection reason (optional, max 1000 chars):');
          if (reason !== null) {
            if (reason.length > 1000) {
              toast.error('Rejection reason must be 1000 characters or less.');
              return;
            }
            rejectPendingUpdate(selectedPendingUpdate, reason);
          }
        }
      },
      'ctrl+r': (e) => {
        e.preventDefault();
        if (!pendingUpdatesLoading && !isManuallyRefreshing) {
          handleManualRefresh();
        }
      },
      'cmd+r': (e) => {
        e.preventDefault();
        if (!pendingUpdatesLoading && !isManuallyRefreshing) {
          handleManualRefresh();
        }
      },
    },
    isAdmin && showPendingModal, // Only enable when modal is open
    [showPendingModal, selectedPendingUpdate, reviewingUpdate, pendingUpdatesLoading, isManuallyRefreshing]
  );

  // Manual refresh handler
  const handleManualRefresh = async () => {
    setIsManuallyRefreshing(true);
    await loadPendingUpdates();
    setLastRefreshTime(new Date());
    setIsManuallyRefreshing(false);
  };

  // Filter and search pending updates
  const filteredPendingUpdates = pendingUpdates.filter(update => {
    // Filter by status
    if (filterStatus !== 'all' && update.status !== filterStatus) {
      return false;
    }

    // Search by email or business name
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const emailMatch = update.client_email?.toLowerCase().includes(query);
      const businessNameMatch = update.mapped_data?.business_name?.toLowerCase().includes(query);
      return emailMatch || businessNameMatch;
    }

    return true;
  });

  // Active (non-deleted) clients for the Clients box
  const activeClients = clients.filter((c) => !c.deleted_at);

  const uniqueEntityTypes = [
    ...new Set(activeClients.map((c) => c.entity_type).filter(Boolean)),
  ].sort();

  const filteredClients = activeClients.filter((client) => {
    if (clientFilterEntityType !== 'all' && client.entity_type !== clientFilterEntityType) {
      return false;
    }
    if (clientSearchQuery.trim()) {
      const q = clientSearchQuery.toLowerCase().trim();
      return (
        client.business_name?.toLowerCase().includes(q) ||
        client.owner_name?.toLowerCase().includes(q) ||
        client.owner_email?.toLowerCase().includes(q) ||
        client.business_email?.toLowerCase().includes(q) ||
        client.login_email?.toLowerCase().includes(q) ||
        client.entity_type?.toLowerCase().includes(q) ||
        client.user_id?.toLowerCase().includes(q) ||
        String(client.id).includes(q)
      );
    }
    return true;
  });

  const clearClientFilters = () => {
    setClientSearchQuery('');
    setClientFilterEntityType('all');
  };

  const loadClients = async () => {
    setClientsLoading(true);
    setClientsError('');

    try {
      await retryOperation(async () => {
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }

        setClients(data || []);
      });
    } catch (error) {
      console.error(error);
      setClientsError('Unable to load clients. Please try again.');
      showActionableError(error, loadClients, 'Loading clients');
    } finally {
      setClientsLoading(false);
    }
  };

  const selectClient = async (client, { navigateToDetail = true } = {}) => {
    const sameClient = selectedClient?.id === client.id;
    setSelectedClient(client);
    if (!sameClient) {
      setActiveClientTab('information');
    }
    setBusinessName(client.business_name || '');
    setEntityType(client.entity_type || '');
    setLoginEmail(client.login_email || client.owner_email || client.business_email || '');
    setStripeCustomerDirect(client.stripe_customer_id_direct || '');
    setStripeCustomerJotform(client.stripe_customer_id_jotform || '');
    setNwraSearchName(client.business_name || '');
    setNwraSearchResults([]);
    setBillingPreview(null);
    // Owner information
    setOwnerName(client.owner_name || '');
    setOwnerEmail(client.owner_email || '');
    setOwnerPhone(client.owner_phone || '');
    setOwnerAddressLine1(client.owner_address_line1 || '');
    setOwnerAddressLine2(client.owner_address_line2 || '');
    setOwnerCity(client.owner_city || '');
    setOwnerState(client.owner_state || '');
    setOwnerZip(client.owner_zip || '');
    // Business information
    setBusinessAddressLine1(client.business_address_line1 || '');
    setBusinessAddressLine2(client.business_address_line2 || '');
    setBusinessCity(client.business_city || '');
    setBusinessState(client.business_state || '');
    setBusinessZip(client.business_zip || '');
    setBusinessPhone(client.business_phone || '');
    setBusinessEmail(client.business_email || '');
    setEin(client.ein || '');
    setRegistrationNumber(client.registration_number || '');
    // Registered agent information
    setRegisteredAgentName(client.registered_agent_name || '');
    setRegisteredAgentAddressLine1(client.registered_agent_address_line1 || '');
    setRegisteredAgentAddressLine2(client.registered_agent_address_line2 || '');
    setRegisteredAgentCity(client.registered_agent_city || '');
    setRegisteredAgentState(client.registered_agent_state || '');
    setRegisteredAgentZip(client.registered_agent_zip || '');
    setRegisteredAgentPhone(client.registered_agent_phone || '');
    setRegisteredAgentEmail(client.registered_agent_email || '');
    setFilingsError('');
    setClientFilings([]);
    setClientFolders([]);
    setNewTitle('');
    setDocumentType('');
    setTag('');
    setCustomDate('');
    setFiles([]);
    setUploadZoneKey((k) => k + 1);
    setProfileChanges(null);
    setProfileChangesError('');
    loadProfileChanges(client.id);

    if (navigateToDetail) {
      navigate(`/admin/clients/${client.id}`);
    }

    if (!client.user_id) {
      setFilingsError('This client has no linked user account. Documents cannot be uploaded until they create an account.');
      return;
    }

    setFilingsLoading(true);
    const [filingsResult, foldersResult] = await Promise.all([
      supabase
        .from('filings')
        .select('*')
        .eq('user_id', client.user_id)
        .order('uploaded_at', { ascending: false }),
      supabase
        .from('folders')
        .select('*')
        .eq('user_id', client.user_id)
        .order('name', { ascending: true }),
    ]);

    if (filingsResult.error) {
      console.error(filingsResult.error);
      setFilingsError('Unable to load filings for this client.');
      toast.error('Failed to load filings');
    } else {
      setClientFilings(filingsResult.data || []);
    }

    if (foldersResult.error) {
      console.error(foldersResult.error);
    } else {
      setClientFolders(foldersResult.data || []);
    }

    setFilingsLoading(false);
  };

  const loadProfileChanges = async (clientId) => {
    if (!clientId) return;
    setProfileChangesLoading(true);
    setProfileChangesError('');
    try {
      const { data, error } = await supabase
        .from('client_profile_changes')
        .select('*')
        .eq('client_id', clientId)
        .order('changed_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      setProfileChanges(data || []);
    } catch (err) {
      console.error('Profile changes load error:', err);
      setProfileChangesError('Change history unavailable');
      setProfileChanges([]);
    } finally {
      setProfileChangesLoading(false);
    }
  };

  const saveClientInfo = async () => {
    if (!selectedClient) return;

    const nameCheck = validateBusinessName(businessName, { required: false });
    if (!nameCheck.isValid) {
      toast.error(nameCheck.error);
      return;
    }
    if (entityType && entityType.includes('@')) {
      toast.error('Entity type cannot be an email address');
      return;
    }

    setSavingClient(true);
    const oldClient = { ...selectedClient };
    const hasCompany = !!(businessName && businessName.trim());
    const updateData = {
      business_name: businessName || null,
      entity_type: entityType || null,
      login_email: loginEmail || null,
      stripe_customer_id_direct: stripeCustomerDirect || null,
      stripe_customer_id_jotform: stripeCustomerJotform || null,
      profile_status: hasCompany ? 'active' : 'incomplete',
      owner_name: ownerName || null,
      owner_email: ownerEmail || null,
      owner_phone: ownerPhone || null,
      owner_address_line1: ownerAddressLine1 || null,
      owner_address_line2: ownerAddressLine2 || null,
      owner_city: ownerCity || null,
      owner_state: ownerState || null,
      owner_zip: ownerZip || null,
      business_address_line1: businessAddressLine1 || null,
      business_address_line2: businessAddressLine2 || null,
      business_city: businessCity || null,
      business_state: businessState || null,
      business_zip: businessZip || null,
      business_phone: businessPhone || null,
      business_email: businessEmail || null,
      ein: ein || null,
      registration_number: registrationNumber || null,
      registered_agent_name: registeredAgentName || null,
      registered_agent_address_line1: registeredAgentAddressLine1 || null,
      registered_agent_address_line2: registeredAgentAddressLine2 || null,
      registered_agent_city: registeredAgentCity || null,
      registered_agent_state: registeredAgentState || null,
      registered_agent_zip: registeredAgentZip || null,
      registered_agent_phone: registeredAgentPhone || null,
      registered_agent_email: registeredAgentEmail || null,
      last_info_update: new Date().toISOString(),
      info_update_requested_at: new Date().toISOString(),
      info_update_requested_by: user?.id || null,
    };

    const { data, error } = await supabase
      .from('clients')
      .update(updateData)
      .eq('id', selectedClient.id)
      .select('*')
      .single();

    if (error) {
      console.error(error);
      toast.error(`Could not save client info: ${error.message}`);
    } else {
      try {
        await auditProfileChanges(supabase, oldClient, updateData, data.id, user?.id || null, 'admin', true);
      } catch (auditErr) {
        console.error('[audit] Admin save audit failed:', auditErr);
      }
      const updated = clients.map((c) => (c.id === data.id ? data : c));
      setClients(updated);
      setSelectedClient(data);
      toast.success('Client information saved successfully');
      loadProfileChanges(data.id);
    }

    setSavingClient(false);
  };

  const uploadForClient = async () => {
    if (!selectedClient) {
      toast.error('Select a client first.');
      return;
    }
    if (!selectedClient.user_id) {
      toast.error('This client has no linked user account. They must create an account before documents can be uploaded.');
      return;
    }
    if (!newTitle || !files.length) {
      toast.error('Title and file are required.');
      return;
    }
    if (tag && tag.length > 30) {
      toast.error('Tag must be 30 characters or less.');
      return;
    }

    setUploading(true);

    const file = files[0];
    const fileExt = file.name.split('.').pop() || 'file';
    const sanitizedTitle = sanitizeFilename(newTitle || 'document');
    const fileName = `${selectedClient.user_id}/${Date.now()}-${sanitizedTitle}.${fileExt}`;

    const rlsHint =
      'Admin upload is blocked by database security rules. Run supabase/fix_admin_filings_rls.sql in the Supabase SQL Editor, or set SUPABASE_SERVICE_ROLE_KEY on Vercel.';

    const finishSuccess = async (filing) => {
      if (filing) {
        setClientFilings([filing, ...clientFilings]);
      }
      if (filing?.folder_id) {
        const { data: foldersData } = await supabase
          .from('folders')
          .select('*')
          .eq('user_id', selectedClient.user_id)
          .order('name', { ascending: true });
        if (foldersData) setClientFolders(foldersData);
      }
      setNewTitle('');
      setDocumentType('');
      setTag('');
      setCustomDate('');
      setFiles([]);
      setUploadZoneKey((k) => k + 1);
      toast.success('Document uploaded successfully. The client can view it in their portal.');
    };

    // Same-origin Vercel API (deploys with the frontend — no separate Supabase function deploy)
    const uploadViaApi = async () => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData?.session?.access_token) {
        throw new Error('Session expired. Please log out and log back in.');
      }

      const formData = new FormData();
      formData.append('user_id', selectedClient.user_id);
      formData.append('title', newTitle);
      if (documentType) formData.append('document_type', documentType);
      if (tag && tag.trim()) formData.append('tag', tag.trim());
      if (customDate) formData.append('custom_date', customDate);
      formData.append('file', file, file.name);

      const response = await fetch('/api/admin-upload-filing', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`,
        },
        body: formData,
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.details || result.error || `Upload failed (${response.status})`);
      }
      return result.filing;
    };

    const isRlsOrDenied = (msg = '') =>
      /row-level security|violates|policy|unauthorized|permission|403|42501/i.test(msg);

    try {
      // Prefer direct upload when RLS allows admins
      const { error: uploadError } = await supabase.storage
        .from('filings')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        const msg = uploadError.message || String(uploadError);
        console.warn('Direct storage upload failed:', uploadError);
        try {
          const filing = await uploadViaApi();
          await finishSuccess(filing);
          return;
        } catch (apiErr) {
          console.error(apiErr);
          if (isRlsOrDenied(msg) || isRlsOrDenied(apiErr.message)) {
            toast.error(rlsHint);
          } else if (/Failed to fetch|NetworkError/i.test(apiErr.message || '')) {
            toast.error(rlsHint);
          } else {
            toast.error(apiErr.message || msg || 'Upload failed.');
          }
          return;
        }
      }

      let folderIdToUse = null;
      if (tag && tag.trim()) {
        const tagTrimmed = sanitizeInput(tag.trim(), 50);
        if (tagTrimmed.length >= 1 && tagTrimmed.length <= 50) {
          const existingFolder = clientFolders.find(
            (f) => f.name.toLowerCase() === tagTrimmed.toLowerCase()
          );
          if (!existingFolder) {
            const { data: newFolder, error: folderError } = await supabase
              .from('folders')
              .insert({
                user_id: selectedClient.user_id,
                name: tagTrimmed,
                description: `Auto-created from tag: ${tagTrimmed}`,
              })
              .select('*')
              .single();
            if (!folderError && newFolder) {
              folderIdToUse = newFolder.id;
              setClientFolders((prev) => [...prev, newFolder]);
            }
          } else {
            folderIdToUse = existingFolder.id;
          }
        }
      }

      const filingPayload = {
        user_id: selectedClient.user_id,
        title: newTitle,
        file_path: fileName,
        document_type: documentType || null,
        folder_id: folderIdToUse,
        tag: tag && tag.trim() ? tag.trim() : null,
        custom_date: customDate || null,
        source: 'admin',
      };

      let { data, error: insertError } = await insertFilingWithFallback(supabase, filingPayload);

      if (insertError) {
        await supabase.storage.from('filings').remove([fileName]);
        const msg = insertError.message || '';
        console.warn('Direct filings insert failed:', insertError);
        try {
          const filing = await uploadViaApi();
          await finishSuccess(filing);
          return;
        } catch (apiErr) {
          console.error(apiErr);
          toast.error(
            isRlsOrDenied(msg) || isRlsOrDenied(apiErr.message)
              ? rlsHint
              : apiErr.message || msg || 'Failed to save document.'
          );
          return;
        }
      }

      await finishSuccess(data);
    } catch (error) {
      console.error(error);
      const msg = error?.message || '';
      toast.error(
        /Failed to fetch|NetworkError|row-level security|policy/i.test(msg) ? rlsHint : msg || 'Upload failed.'
      );
    } finally {
      setUploading(false);
    }
  };

  const pullNWRADocuments = async () => {
    if (!selectedClient) {
      toast.error('Select a client first.');
      return;
    }
    if (!selectedClient.user_id) {
      toast.error('This client has no linked user account.');
      return;
    }
    if (!selectedClient.nwra_company_id) {
      toast.error('This client is not linked to an NWRA company. Submit a filing first or set nwra_company_id.');
      return;
    }

    setSyncingNWRA(true);
    toast.loading('Pulling documents from NWRA...', { id: 'nwra-sync' });

    try {
      const { data, error } = await supabase.functions.invoke('sync-nwra-documents', {
        body: { client_id: selectedClient.id },
      });

      if (error) {
        throw new Error(error.message || 'Failed to sync NWRA documents');
      }
      if (data?.error) {
        throw new Error(data.error);
      }

      const imported = data?.imported ?? 0;
      const skipped = data?.skipped ?? 0;
      toast.success(
        `NWRA sync complete: ${imported} new document${imported === 1 ? '' : 's'}, ${skipped} already synced.`,
        { id: 'nwra-sync' }
      );

      // Reload filings for this client
      const { data: filingsData, error: filingsErr } = await supabase
        .from('filings')
        .select('*')
        .eq('user_id', selectedClient.user_id)
        .order('uploaded_at', { ascending: false });

      if (!filingsErr) {
        setClientFilings(filingsData || []);
      }
    } catch (err) {
      console.error('NWRA document sync error:', err);
      toast.error(err.message || 'Failed to pull documents from NWRA', { id: 'nwra-sync' });
    } finally {
      setSyncingNWRA(false);
    }
  };

  const searchNWRACompanies = async () => {
    const name = (nwraSearchName || businessName || '').trim();
    if (!name) {
      toast.error('Enter a company name to search on NWRA');
      return;
    }
    setNwraSearching(true);
    setNwraSearchResults([]);
    try {
      const { data, error } = await supabase.functions.invoke('find-nwra-companies', {
        body: { name },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      setNwraSearchResults(data?.companies || []);
      if (!(data?.companies || []).length) {
        toast('No NWRA companies matched that name', { icon: 'ℹ️' });
      }
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'NWRA company search failed');
    } finally {
      setNwraSearching(false);
    }
  };

  const linkNWRACompany = async (companyId, companyName) => {
    if (!selectedClient || !companyId) return;
    try {
      const { data, error } = await supabase
        .from('clients')
        .update({
          nwra_company_id: String(companyId),
          business_name: businessName || companyName || selectedClient.business_name,
          profile_status: 'active',
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedClient.id)
        .select('*')
        .single();
      if (error) throw error;
      setSelectedClient(data);
      setClients((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      toast.success(`Linked NWRA company ${companyId}`);
      setNwraSearchResults([]);
    } catch (err) {
      toast.error(err.message || 'Failed to link NWRA company');
    }
  };

  const syncNWRACompany = async () => {
    if (!selectedClient?.nwra_company_id) {
      toast.error('Link an NWRA company first');
      return;
    }
    setSyncingNWRACompany(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-nwra-company', {
        body: { client_id: selectedClient.id },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      if (data?.client) {
        setSelectedClient(data.client);
        setClients((prev) => prev.map((c) => (c.id === data.client.id ? data.client : c)));
        setBusinessName(data.client.business_name || '');
        setEntityType(data.client.entity_type || '');
        setRegisteredAgentName(data.client.registered_agent_name || '');
        setRegisteredAgentAddressLine1(data.client.registered_agent_address_line1 || '');
        setRegisteredAgentCity(data.client.registered_agent_city || '');
        setRegisteredAgentState(data.client.registered_agent_state || '');
        setRegisteredAgentZip(data.client.registered_agent_zip || '');
        setBusinessAddressLine1(data.client.business_address_line1 || '');
        setBusinessCity(data.client.business_city || '');
        setBusinessState(data.client.business_state || '');
        setBusinessZip(data.client.business_zip || '');
      }
      toast.success('Company info synced from NWRA');
    } catch (err) {
      toast.error(err.message || 'Failed to sync company from NWRA');
    } finally {
      setSyncingNWRACompany(false);
    }
  };

  const linkPortalUser = async () => {
    if (!selectedClient) return;
    const email = (loginEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter the client’s portal login email first');
      return;
    }
    setLinkingPortalUser(true);
    try {
      const { data, error } = await supabase.functions.invoke('link-portal-user', {
        body: { client_id: selectedClient.id, email },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      if (data?.client) {
        await selectClient(data.client, { navigateToDetail: false });
        setLoginEmail(data.client.login_email || email);
      }
      toast.success(`Linked portal login ${data?.user?.email || email} to this company`);
    } catch (err) {
      toast.error(err.message || 'Failed to link portal login');
    } finally {
      setLinkingPortalUser(false);
    }
  };

  const loadClientBilling = async () => {
    if (!selectedClient?.user_id && !loginEmail) {
      toast.error('Client needs a login email to look up Stripe');
      return;
    }
    setBillingLoading(true);
    setBillingPreview(null);
    try {
      const { data, error } = await supabase.functions.invoke('stripe-billing', {
        body: {
          user_id: selectedClient.user_id,
          email: loginEmail || selectedClient.login_email,
          client_id: selectedClient.id,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      setBillingPreview(data);
    } catch (err) {
      toast.error(err.message || 'Billing lookup failed (are Stripe keys configured?)');
    } finally {
      setBillingLoading(false);
    }
  };

  const getClientStatusBadges = (client) => {
    const badges = [];
    if (!client.business_name || client.profile_status === 'incomplete') {
      badges.push({ label: 'Incomplete', color: colors.warning, text: colors.darkGray });
    } else {
      badges.push({ label: 'Active', color: '#e8f5e0', text: colors.green });
    }
    if (client.user_id) {
      badges.push({ label: 'Portal login', color: '#e8f5e0', text: colors.green });
    } else {
      badges.push({ label: 'No login', color: '#fff5f5', text: colors.error });
    }
    if (client.nwra_company_id) {
      badges.push({ label: 'NWRA', color: '#e6f2fa', text: colors.blue });
    }
    if (client.stripe_customer_id_direct || client.stripe_customer_id_jotform) {
      badges.push({ label: 'Stripe', color: '#f3e8ff', text: '#6b21a8' });
    }
    return badges;
  };

  const handleDeleteClick = (filing) => {
    setFilingToDelete(filing);
    setDeleteModalOpen(true);
  };

  const deleteFiling = async () => {
    if (!filingToDelete || !selectedClient) return;

    const { error: storageError } = await supabase.storage
      .from('filings')
      .remove([filingToDelete.file_path]);

    if (storageError) {
      console.error(storageError);
      toast.error(storageError.message);
      setDeleteModalOpen(false);
      return;
    }

    const { error: dbError } = await supabase
      .from('filings')
      .delete()
      .eq('id', filingToDelete.id);

    if (dbError) {
      console.error(dbError);
      toast.error(dbError.message);
    } else {
      setClientFilings(clientFilings.filter((f) => f.id !== filingToDelete.id));
      toast.success('Document deleted successfully');
    }

    setDeleteModalOpen(false);
    setFilingToDelete(null);
  };

  const getDownloadUrl = async (filePath) => {
    const { data, error } = await supabase.storage
      .from('filings')
      .createSignedUrl(filePath, 600);

    if (error) {
      console.error(error);
      toast.error(error.message);
      return null;
    }

    return data.signedUrl;
  };

  // Load pending updates
  const loadPendingUpdates = async () => {
    setPendingUpdatesLoading(true);
    try {
      await retryOperation(async () => {
        const { data, error } = await supabase
          .from('pending_client_updates')
          .select('*')
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (error) {
          throw error;
        }

        setPendingUpdates(data || []);
        setLastRefreshTime(new Date());
      });
    } catch (error) {
      console.error(error);
      showActionableError(error, loadPendingUpdates, 'Loading pending updates');
    } finally {
      setPendingUpdatesLoading(false);
    }
  };

  // Approve pending update
  const approvePendingUpdate = async (pendingUpdate) => {
    setReviewingUpdate(true);
    
    // Store previous state for undo
    const previousPendingUpdates = [...pendingUpdates];
    const previousSelectedUpdate = selectedPendingUpdate;
    
    // Optimistic update: remove from list immediately
    setPendingUpdates(prev => prev.filter(u => u.id !== pendingUpdate.id));
    toast.loading('Approving...', { id: 'approving' });
    
    try {
      await retryOperation(async () => {
        // SECURITY: Verify admin status before making request
        if (!user || !isAdmin) {
          throw new Error('You do not have permission to approve updates.');
        }

        // Call the Supabase Edge Function to securely approve and move data
        // The Edge Function will verify admin status server-side
        const { data, error } = await supabase.functions.invoke('approve-pending-client', {
          body: {
            pendingUpdateId: pendingUpdate.id,
            reviewedBy: user.id
          }
        });

        if (error) {
          throw new Error(error.message || 'Failed to approve client');
        }

        if (data.error) {
          throw new Error(data.error);
        }

        toast.dismiss('approving');
        
        // Show undoable success message
        await createUndoableAction(
          async () => {
            // Action already completed, just show message
            return data;
          },
          async () => {
            // SECURITY: Verify admin status before undo
            if (!user || !isAdmin) {
              toast.error('You no longer have permission to undo this action.');
              return;
            }

            // SECURITY: Verify the pending update still exists and is approved
            const { data: currentUpdate, error: fetchError } = await supabase
              .from('pending_client_updates')
              .select('status, reviewed_by')
              .eq('id', pendingUpdate.id)
              .single();

            if (fetchError || !currentUpdate) {
              toast.error('Cannot undo: Update no longer exists.');
              return;
            }

            if (currentUpdate.status !== 'approved') {
              toast.error(`Cannot undo: Update is now ${currentUpdate.status}, not approved.`);
              return;
            }

            if (currentUpdate.reviewed_by !== user.id) {
              toast.error('Cannot undo: You did not approve this update.');
              return;
            }
            
            // Undo: revert status back to pending
            const { error: undoError } = await supabase
              .from('pending_client_updates')
              .update({
                status: 'pending',
                reviewed_at: null,
                reviewed_by: null,
              })
              .eq('id', pendingUpdate.id);
            
            if (undoError) {
              toast.error(`Failed to undo: ${undoError.message}`);
              if (process.env.NODE_ENV === 'development') {
                console.error('Undo error:', undoError);
              }
            } else {
              // Restore to pending updates list
              setPendingUpdates(previousPendingUpdates);
              // If this was the selected update, restore it
              if (previousSelectedUpdate?.id === pendingUpdate.id) {
                setSelectedPendingUpdate(previousSelectedUpdate);
              }
              // Reload to ensure consistency
              loadPendingUpdates();
              loadClients();
            }
          },
          data.message || 'Client information updated successfully',
          10000 // 10 seconds for undo window
        );
        
        loadPendingUpdates();
        loadClients(); // Refresh client list
        setShowPendingModal(false);
      });
    } catch (error) {
      // Rollback optimistic update
      setPendingUpdates(previousPendingUpdates);
      toast.dismiss('approving');
      console.error(error);
      showActionableError(error, () => approvePendingUpdate(pendingUpdate), 'Approving submission');
    } finally {
      setReviewingUpdate(false);
    }
  };

  // Load NWRA orders
  const loadNWRAOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('nwra_orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error loading NWRA orders:', error);
      } else {
        setNwraOrders(data || []);
      }
    } catch (error) {
      console.error('Error loading NWRA orders:', error);
    }
  };

  // Get NWRA order for a pending update or client
  const getNWRAOrder = (pendingUpdateId, clientId) => {
    if (pendingUpdateId) {
      return nwraOrders.find(o => o.pending_update_id === pendingUpdateId);
    }
    if (clientId) {
      return nwraOrders.find(o => o.client_id === clientId);
    }
    return null;
  };

  // Submit to NWRA
  const submitToNWRA = async (pendingUpdate) => {
    if (!user || !isAdmin) {
      toast.error('You do not have permission to submit to NWRA.');
      return;
    }

    setSubmittingToNWRA(true);
    toast.loading('Submitting to NWRA...', { id: 'nwra-submit' });

    try {
      const { data, error } = await supabase.functions.invoke('submit-to-nwra-manual', {
        body: {
          pending_update_id: pendingUpdate.id,
          filing_type: pendingUpdate.mapped_data?.service_type || 'Form a Company'
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to submit to NWRA');
      }

      if (data.error) {
        throw new Error(data.error);
      }

      toast.dismiss('nwra-submit');
      toast.success('Successfully submitted to NWRA');
      
      // Reload orders
      loadNWRAOrders();
      loadPendingUpdates();
    } catch (error) {
      toast.dismiss('nwra-submit');
      console.error('NWRA submission error:', error);
      toast.error(`Failed to submit to NWRA: ${error.message}`);
    } finally {
      setSubmittingToNWRA(false);
    }
  };

  // Retry failed NWRA order
  const handleRetryNWRA = async (nwraOrder) => {
    if (!user || !isAdmin) {
      toast.error('You do not have permission to retry NWRA orders.');
      return;
    }
    const maxRetries = nwraOrder.max_retries ?? 3;
    if ((nwraOrder.retry_count ?? 0) >= maxRetries) {
      toast.error(`Max retries (${maxRetries}) exceeded for this order.`);
      return;
    }
    setRetryingOrderId(nwraOrder.id);
    toast.loading('Retrying NWRA submission...', { id: 'nwra-retry' });
    try {
      const { data, error } = await supabase.functions.invoke('submit-to-nwra-manual', {
        body: { order_id: nwraOrder.id }
      });
      if (error) throw new Error(error.message || 'Failed to retry');
      if (data.error) throw new Error(data.error);
      toast.dismiss('nwra-retry');
      toast.success('Retry submitted to NWRA');
      loadNWRAOrders();
      loadPendingUpdates();
    } catch (err) {
      toast.dismiss('nwra-retry');
      console.error('NWRA retry error:', err);
      toast.error(`Retry failed: ${err.message}`);
    } finally {
      setRetryingOrderId(null);
    }
  };

  // Mark as Direct Filing
  const markAsDirectFiling = async (pendingUpdate) => {
    if (!user || !isAdmin) {
      toast.error('You do not have permission to mark as direct filing.');
      return;
    }

    setMarkingDirectFiling(true);
    toast.loading('Marking as direct filing...', { id: 'direct-filing' });

    try {
      // Get or create client_id
      let clientId = null;
      if (pendingUpdate.user_id) {
        const { data: client } = await supabase
          .from('clients')
          .select('id')
          .eq('user_id', pendingUpdate.user_id)
          .single();
        
        if (client) {
          clientId = client.id;
        }
      }

      // Check if order already exists
      const { data: existingOrder } = await supabase
        .from('nwra_orders')
        .select('id')
        .eq('pending_update_id', pendingUpdate.id)
        .single();

      if (existingOrder) {
        // Update existing order
        const { error: updateError } = await supabase
          .from('nwra_orders')
          .update({
            filing_method: 'direct_filing',
            status: 'pending',
            current_step: 'admin_filing_directly',
            admin_notes: 'Admin will file directly with Secretary of State',
            updated_at: new Date().toISOString()
          })
          .eq('id', existingOrder.id)
          .select()
          .single();

        if (updateError) {
          throw new Error(updateError.message || 'Failed to update order record');
        }
      } else {
        // Create new order
        const { error: insertError } = await supabase
          .from('nwra_orders')
          .insert({
            client_id: clientId,
            pending_update_id: pendingUpdate.id,
            filing_type: pendingUpdate.mapped_data?.service_type || 'Form a Company',
            filing_method: 'direct_filing',
            status: 'pending',
            current_step: 'admin_filing_directly',
            admin_notes: 'Admin will file directly with Secretary of State'
          })
          .select()
          .single();

        if (insertError) {
          throw new Error(insertError.message || 'Failed to create order record');
        }
      }

      toast.dismiss('direct-filing');
      toast.success('Marked as direct filing');
      
      // Reload orders
      loadNWRAOrders();
      loadPendingUpdates();
    } catch (error) {
      toast.dismiss('direct-filing');
      console.error('Direct filing error:', error);
      toast.error(`Failed to mark as direct filing: ${error.message}`);
    } finally {
      setMarkingDirectFiling(false);
    }
  };

  // Test NWRA Connection
  const testNWRAConnection = async () => {
    if (!user || !isAdmin) {
      toast.error('You do not have permission to test NWRA connection.');
      return;
    }

    setTestingNWRA(true);
    toast.loading('Testing NWRA connection...', { id: 'nwra-test' });

    try {
      // First, verify we have a valid session
      const { data: sessionCheck, error: sessionCheckError } = await supabase.auth.getSession();
      if (sessionCheckError || !sessionCheck?.session) {
        throw new Error('No active session. Please log out and log back in.');
      }
      
      console.log('Session check passed:', {
        hasSession: !!sessionCheck.session,
        hasToken: !!sessionCheck.session.access_token,
        userId: sessionCheck.session.user?.id,
        userRole: sessionCheck.session.user?.user_metadata?.role
      });
      
      // Try using Supabase's built-in invoke method first (handles auth automatically)
      let data, error;
      
      try {
        console.log('Attempting to call NWRA test function via Supabase invoke...');
        const result = await supabase.functions.invoke('test-nwra-connection', {
          body: {}
        });
        
        console.log('Supabase invoke result:', { 
          hasData: !!result.data, 
          hasError: !!result.error,
          error: result.error,
          data: result.data
        });
        
        data = result.data;
        error = result.error;
        
        // Check if data contains an error (sometimes errors are in data)
        if (data && data.error) {
          console.log('Error found in data:', data.error);
          error = { message: data.error, context: { body: data } };
        }
        
        // If invoke worked without error, we're done
        if (!error && data && !data.error) {
          console.log('Invoke succeeded, using result');
          // Success with invoke method - will skip the catch block
        } else if (error || (data && data.error)) {
          // Invoke returned an error, try direct fetch as fallback
          console.log('Invoke returned error, trying direct fetch. Error:', error || data?.error);
          throw new Error('Invoke returned error, trying direct fetch...');
        } else {
          // No data and no error - try direct fetch
          console.log('No data from invoke, trying direct fetch');
          throw new Error('No data from invoke, trying direct fetch...');
        }
      } catch (invokeError) {
        console.log('Supabase invoke failed, trying direct fetch:', invokeError);
        
        // Fallback to direct fetch with explicit token
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          throw new Error(`Session error: ${sessionError.message}. Please log out and log back in.`);
        }
        
        if (!sessionData?.session) {
          throw new Error('No active session. Please log out and log back in.');
        }

        const accessToken = sessionData.session.access_token;
        if (!accessToken) {
          throw new Error('No access token found in session. Please log out and log back in.');
        }
        
        // Verify user is still an admin
        const userRole = sessionData.session.user?.user_metadata?.role;
        if (userRole !== 'admin') {
          throw new Error('Admin access required. Your account does not have admin privileges.');
        }

        // Use direct fetch to ensure Authorization header is properly passed
        const functionUrl = `${SUPABASE_URL}/functions/v1/test-nwra-connection`;
        
        console.log('Calling NWRA test function via direct fetch...', {
          hasToken: !!accessToken,
          tokenLength: accessToken?.length,
          functionUrl
        });
        
        const response = await fetch(functionUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY
          },
          body: JSON.stringify({})
        });

        const responseText = await response.text();
        let responseData;
        try {
          responseData = JSON.parse(responseText);
        } catch (e) {
          responseData = { raw: responseText, error: 'Failed to parse response' };
        }

        if (!response.ok) {
          const errorMessage = responseData.error || responseData.details || responseText;
          
          // Provide more specific error messages for 401
          if (response.status === 401) {
            if (errorMessage.includes('No authentication token')) {
              error = {
                message: 'Authentication failed: No token provided. Please log out and log back in.',
                context: {
                  status: response.status,
                  body: responseData
                }
              };
            } else if (errorMessage.includes('Invalid authentication token')) {
              error = {
                message: 'Authentication failed: Invalid or expired token. Please log out and log back in.',
                context: {
                  status: response.status,
                  body: responseData
                }
              };
            } else {
              error = {
                message: `Authentication failed: ${errorMessage}. Please log out and log back in.`,
                context: {
                  status: response.status,
                  body: responseData
                }
              };
            }
          } else if (response.status === 403) {
            error = {
              message: 'Access denied: Admin privileges required. Please ensure your account has admin role.',
              context: {
                status: response.status,
                body: responseData
              }
            };
          } else {
            error = {
              message: `HTTP ${response.status}: ${errorMessage}`,
              context: {
                status: response.status,
                body: responseData
              }
            };
          }
          data = responseData; // May contain error details
        } else {
          data = responseData;
          error = null;
        }
      }

      // Even on non-2xx status, data might contain the error response body
      if (data && data.error) {
        const errorMsg = typeof data.error === 'string' ? data.error : data.error.message || JSON.stringify(data.error);
        throw new Error(errorMsg);
      }

      if (error) {
        // Check multiple possible locations for error message
        let errorMessage = 'Failed to test NWRA connection';
        let errorDetails = null;
        
        // Log the full error structure for debugging
        console.error('NWRA test error - full error object:', error);
        console.error('NWRA test error - error keys:', Object.keys(error || {}));
        console.error('NWRA test error - error context:', error?.context);
        console.error('NWRA test error - data (may contain error):', data);
        
        // Try to extract error from various possible locations
        // Sometimes Supabase returns the error body in data even when error is set
        if (data) {
          if (typeof data === 'string') {
            try {
              const parsed = JSON.parse(data);
              errorMessage = parsed.error || parsed.details || parsed.message || errorMessage;
              errorDetails = parsed;
            } catch (e) {
              errorMessage = data;
            }
          } else if (data.error) {
            errorMessage = typeof data.error === 'string' ? data.error : (data.error.message || JSON.stringify(data.error));
            errorDetails = data;
          } else if (data.details) {
            errorMessage = typeof data.details === 'string' ? data.details : JSON.stringify(data.details);
            errorDetails = data;
          }
        }
        
        // Check error.context for response body
        if (!errorDetails && error.context) {
          if (error.context.body) {
            try {
              const body = typeof error.context.body === 'string' 
                ? JSON.parse(error.context.body) 
                : error.context.body;
              errorMessage = body?.error || body?.details || body?.message || errorMessage;
              errorDetails = body;
            } catch (e) {
              // If parsing fails, use the raw body
              errorMessage = error.context.body;
            }
          }
          
          if (error.context.status) {
            errorMessage = `HTTP ${error.context.status}: ${errorMessage}`;
          }
        }
        
        // Fallback to error.message if we still don't have a good message
        if (errorMessage === 'Failed to test NWRA connection' && error.message) {
          errorMessage = error.message;
        }
        
        // If we still don't have details, try to stringify the error
        if (!errorDetails) {
          try {
            errorDetails = JSON.stringify(error, null, 2);
          } catch (e) {
            errorDetails = String(error);
          }
        }
        
        console.error('NWRA test error - extracted message:', errorMessage);
        console.error('NWRA test error - error details:', errorDetails);
        
        throw new Error(errorMessage);
      }

      if (!data) {
        throw new Error('No data returned from test');
      }

      toast.dismiss('nwra-test');
      setNwraTestResults(data);
      
      // Show summary
      const summary = data.summary || {};
      if (summary.all_passed) {
        toast.success(`NWRA connection test passed! Found ${data.companies?.length || 0} companies.`);
      } else {
        toast.warning(`NWRA connection test completed with ${summary.failed || 0} failure(s).`);
      }
    } catch (error) {
      toast.dismiss('nwra-test');
      
      // Log comprehensive error information
      console.error('NWRA test catch block - error:', error);
      console.error('NWRA test catch block - error type:', typeof error);
      console.error('NWRA test catch block - error keys:', Object.keys(error || {}));
      if (error?.stack) {
        console.error('NWRA test catch block - error stack:', error.stack);
      }
      
      // Extract the actual error message from multiple possible locations
      let errorMessage = 'Unknown error occurred';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.error) {
        errorMessage = typeof error.error === 'string' ? error.error : JSON.stringify(error.error);
      } else if (error?.details) {
        errorMessage = typeof error.details === 'string' ? error.details : JSON.stringify(error.details);
      } else if (error?.toString && error.toString() !== '[object Object]') {
        errorMessage = error.toString();
      } else {
        // Last resort: try to stringify the whole error
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = String(error);
        }
      }
      
      console.error('NWRA test catch block - final error message:', errorMessage);
      
      toast.error(`Failed to test NWRA connection: ${errorMessage}`);
      setNwraTestResults({ error: errorMessage });
    } finally {
      setTestingNWRA(false);
    }
  };

  // Reject pending update
  const rejectPendingUpdate = async (pendingUpdate, reason) => {
    // SECURITY: Verify admin status before allowing rejection
    if (!user || !isAdmin) {
      toast.error('You do not have permission to reject updates.');
      return;
    }

    // Validate rejection reason length
    if (reason && reason.length > 1000) {
      toast.error('Rejection reason must be 1000 characters or less.');
      return;
    }

    const { error } = await supabase
      .from('pending_client_updates')
      .update({
        status: 'rejected',
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        rejection_reason: reason || null,
      })
      .eq('id', pendingUpdate.id);

    if (error) {
      toast.error(`Failed to reject: ${error.message}`);
    } else {
      toast.success('Update rejected');
      loadPendingUpdates();
      setShowPendingModal(false);
    }
  };

  if (loadingUser) {
    return (
      <div style={styles.container}>
        <PortalHeader user={user} showAdminLink={false} />
        <div style={styles.content}>
          <Card>
            <LoadingSpinner text="Loading admin panel…" />
          </Card>
        </div>
        <PortalFooter />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div style={styles.container}>
        <PortalHeader user={user} showAdminLink={false} />
        <div style={styles.content}>
          <div style={styles.header}>
            <h1 style={{ fontFamily: fonts.heading, fontSize: '1.75rem', fontWeight: 600, color: colors.darkGray, margin: 0 }}>
              Admin
            </h1>
            <span style={styles.badge}>Restricted</span>
          </div>
          <Card>
            <p style={{ color: colors.midGray, marginBottom: spacing.md }}>
              You do not have access to the admin panel. If this is unexpected, contact the site owner.
            </p>
            <a
              href="https://businessinitiative.org/contact/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.blue,
                textDecoration: 'none',
                fontFamily: fonts.heading,
                fontWeight: 500,
              }}
            >
              Contact Support →
            </a>
          </Card>
        </div>
        <PortalFooter />
      </div>
    );
  }

  const clientsTableColumns = [
    {
      key: 'business_name',
      label: 'Business',
      width: '32%',
      render: (value) => (
        <span className="truncate-cell" title={value || '—'}>
          {value || <em style={{ color: colors.midGray }}>No company yet</em>}
        </span>
      ),
    },
    {
      key: 'entity_type',
      label: 'Type',
      width: '18%',
      render: (value) => (
        <span className="truncate-cell">{value || '—'}</span>
      ),
    },
    {
      key: 'login_email',
      label: 'Email',
      width: '28%',
      render: (value, row) => {
        const email = value || row.owner_email || row.business_email || '—';
        return <span className="truncate-cell" title={email}>{email}</span>;
      },
    },
    {
      key: 'status',
      label: 'Status',
      width: '22%',
      render: (_, row) => (
        <div>
          {getClientStatusBadges(row).map((b) => (
            <span
              key={b.label}
              style={{
                ...styles.statusPill,
                backgroundColor: b.color,
                color: b.text,
              }}
            >
              {b.label}
            </span>
          ))}
        </div>
      ),
    },
  ];

  const filingsTableColumns = [
    {
      key: 'title',
      label: 'Title',
      render: (value) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
          <Icon name="document" type="document" size={18} style={{ color: colors.midGray }} />
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: 'document_type',
      label: 'Type',
      render: (value) => value || '—',
    },
    {
      key: 'source',
      label: 'Source',
      render: (value) => {
        const label = value === 'nwra' ? 'NWRA' : value === 'admin' ? 'Admin' : value === 'manual' ? 'Client' : value || '—';
        return <span style={{ fontSize: '0.875rem' }}>{label}</span>;
      },
    },
    {
      key: 'uploaded_at',
      label: 'Date',
      render: (value) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
          <Button
            size="sm"
            variant="primary"
            onClick={async (e) => {
              e?.stopPropagation?.();
              const url = await getDownloadUrl(row.file_path);
              if (url) window.open(url, '_blank');
            }}
            icon={<Icon name="download" type="action" size={16} />}
            aria-label={`Download ${row.title}`}
          >
            Download
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={(e) => {
              e?.stopPropagation?.();
              handleDeleteClick(row);
            }}
            icon={<Icon name="delete" type="action" size={16} />}
            aria-label={`Delete ${row.title}`}
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={styles.container} className="admin-shell">
      <PortalHeader user={user} showAdminLink={true} />
      <div style={styles.content}>
        <div style={styles.header}>
          <div>
            <h1 style={{ fontFamily: fonts.heading, fontSize: '1.75rem', fontWeight: 600, color: colors.darkGray, margin: 0 }}>
              Admin Dashboard
            </h1>
            <p style={{ color: colors.midGray, margin: 0 }}>Manage clients, submissions, and NWRA links.</p>
          </div>
          <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={styles.badge}>Admin</span>
            <Link
              to="/"
              style={{
                fontFamily: fonts.heading,
                fontSize: '0.875rem',
                color: colors.blue,
                textDecoration: 'none',
              }}
            >
              ← Back to Portal
            </Link>
          </div>
        </div>

        <AdminNav
          pendingCount={pendingUpdates.filter((u) => u.status === 'pending').length}
          unmatchedCount={unmatchedIncomingCount}
        />

        <Routes>
          <Route path="/" element={<Navigate to="matching/incoming" replace />} />
          <Route path="matching/*" element={<MatchingPortal />} />
          <Route path="nwra" element={renderNWRASection()} />
          <Route path="queue" element={renderQueueSection()} />
          <Route path="clients" element={renderClientsSection()} />
          <Route path="clients/:clientId" element={renderClientsSection()} />
          <Route path="*" element={<Navigate to="matching/incoming" replace />} />
        </Routes>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setFilingToDelete(null);
        }}
        title="Delete Filing"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setDeleteModalOpen(false);
                setFilingToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={deleteFiling}>
              Delete
            </Button>
          </>
        }
      >
        <p style={{ color: colors.darkGray, marginBottom: spacing.md }}>
          Are you sure you want to delete <strong>{filingToDelete?.title}</strong>? This action cannot be undone.
        </p>
      </Modal>

      {/* Pending Update Review Modal */}
      <Modal
        isOpen={showPendingModal}
        onClose={() => {
          setShowPendingModal(false);
          setSelectedPendingUpdate(null);
        }}
        title="Review Pending Update"
        size="lg"
        footer={
          selectedPendingUpdate?.status === 'pending' ? (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowPendingModal(false);
                  setSelectedPendingUpdate(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  const reason = prompt('Rejection reason (optional, max 1000 chars):');
                  if (reason !== null) {
                    if (reason.length > 1000) {
                      toast.error('Rejection reason must be 1000 characters or less.');
                      return;
                    }
                    rejectPendingUpdate(selectedPendingUpdate, reason);
                  }
                }}
                disabled={reviewingUpdate}
              >
                Reject
              </Button>
              <Button
                variant="primary"
                onClick={() => approvePendingUpdate(selectedPendingUpdate)}
                loading={reviewingUpdate}
                disabled={!selectedPendingUpdate?.user_id}
                title={!selectedPendingUpdate?.user_id ? 'User must create an account first' : ''}
              >
                Approve & Apply
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              onClick={() => {
                setShowPendingModal(false);
                setSelectedPendingUpdate(null);
              }}
            >
              Close
            </Button>
          )
        }
      >
          {selectedPendingUpdate && (
            <div>
              <div style={{ marginBottom: spacing.lg }}>
                <div style={{ fontFamily: fonts.heading, fontWeight: 600, color: colors.darkGray, marginBottom: spacing.xs }}>
                  Client Email: {selectedPendingUpdate.client_email}
                </div>
                <div style={{ fontSize: '0.875rem', color: colors.midGray, marginBottom: spacing.xs }}>
                  Submitted: {new Date(selectedPendingUpdate.created_at).toLocaleString()}
                </div>
                <div style={{ fontSize: '0.875rem', color: selectedPendingUpdate.user_id ? colors.green : colors.red }}>
                  {selectedPendingUpdate.user_id ? 'User account found' : 'User account not found — client must sign up with this email'}
                </div>
              </div>

              {selectedPendingUpdate.status === 'approved' && (() => {
                const nwraOrder = nwraOrders.find(o => o.pending_update_id === selectedPendingUpdate.id);
                if (nwraOrder) {
                  return (
                    <div style={{ 
                      marginBottom: spacing.lg, 
                      padding: spacing.md, 
                      backgroundColor: nwraOrder.status === 'completed' ? '#e8f5e9' : nwraOrder.status === 'failed' ? '#ffebee' : '#fff3e0',
                      borderRadius: layout.borderRadius,
                      border: `1px solid ${nwraOrder.status === 'completed' ? colors.green : nwraOrder.status === 'failed' ? colors.error : colors.warning}`
                    }}>
                      <div style={{ fontFamily: fonts.heading, fontWeight: 600, marginBottom: spacing.sm }}>
                        NWRA Submission Status: {nwraOrder.status}
                      </div>
                      {nwraOrder.nwra_order_id && (
                        <div style={{ fontSize: '0.875rem', marginBottom: spacing.xs }}>
                          Order ID: <span style={{ fontFamily: 'monospace' }}>{nwraOrder.nwra_order_id}</span>
                        </div>
                      )}
                      {nwraOrder.error_message && (
                        <div style={{ fontSize: '0.875rem', color: colors.error, marginBottom: spacing.sm }}>
                          Error: {nwraOrder.error_message}
                        </div>
                      )}
                      {nwraOrder.status === 'failed' && (
                        <div style={{ display: 'flex', gap: spacing.sm, marginTop: spacing.sm, flexWrap: 'wrap' }}>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => handleRetryNWRA(nwraOrder)}
                            disabled={retryingOrderId === nwraOrder.id}
                            loading={retryingOrderId === nwraOrder.id}
                          >
                            {retryingOrderId === nwraOrder.id ? 'Retrying...' : 'Retry submission'}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}

              <div style={{ marginBottom: spacing.lg }}>
                <h3 style={{ fontFamily: fonts.heading, fontSize: '1rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.sm }}>
                  Data to Apply
                </h3>
                <div style={{ 
                  background: colors.gray100 || '#f5f5f5', 
                  padding: spacing.md, 
                  borderRadius: layout.borderRadius,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  overflow: 'auto',
                  maxHeight: '300px'
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(selectedPendingUpdate.mapped_data, null, 2)}
                  </pre>
                </div>
              </div>

              <details style={{ marginBottom: spacing.lg }}>
                <summary style={{ fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 600, color: colors.darkGray, cursor: 'pointer', marginBottom: spacing.sm }}>
                  View Raw JotForm Data
                </summary>
                <div style={{ 
                  background: colors.gray100 || '#f5f5f5', 
                  padding: spacing.md, 
                  borderRadius: layout.borderRadius,
                  fontSize: '0.875rem',
                  fontFamily: 'monospace',
                  overflow: 'auto',
                  maxHeight: '300px',
                  marginTop: spacing.sm
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(selectedPendingUpdate.jotform_data, null, 2)}
                  </pre>
                </div>
              </details>
            </div>
          )}
      </Modal>
    </div>
  );

  function renderNWRASection() {
    return (
      <Card title="NWRA API Connection" hoverable={false} style={{ marginBottom: spacing.lg }}>
        <div style={{ display: 'flex', gap: spacing.md, alignItems: 'center', marginBottom: spacing.md }}>
          <Button
            variant="outline"
            onClick={testNWRAConnection}
            disabled={testingNWRA}
            loading={testingNWRA}
          >
            {testingNWRA ? 'Testing...' : 'Test NWRA Connection'}
          </Button>
          <span style={{ fontSize: '0.875rem', color: colors.midGray }}>
            Verify API access and view your companies
          </span>
        </div>
        {nwraTestResults && (
          <div style={{ 
            marginTop: spacing.md, 
            padding: spacing.md, 
            backgroundColor: colors.gray100 || '#f5f5f5', 
            borderRadius: layout.borderRadius,
            fontSize: '0.875rem'
          }}>
            <div style={{ marginBottom: spacing.sm, fontWeight: 600 }}>Test Results:</div>
            {nwraTestResults.error ? (
              <div style={{ color: colors.error }}>Error: {nwraTestResults.error}</div>
            ) : (
              <>
                {nwraTestResults.website_id && (
                  <div style={{ marginBottom: spacing.xs }}>
                    <strong>Website ID:</strong>{' '}
                    <span style={{ fontFamily: 'monospace' }}>{nwraTestResults.website_id}</span>
                  </div>
                )}
                {nwraTestResults.companies && (
                  <div style={{ marginBottom: spacing.xs }}>
                    <strong>Companies Found:</strong> {nwraTestResults.companies.length}
                  </div>
                )}
                {nwraTestResults.summary && (
                  <div style={{ marginBottom: spacing.sm }}>
                    <strong>Tests:</strong> {nwraTestResults.summary.successful}/{nwraTestResults.summary.total_tests} passed
                  </div>
                )}
                <details style={{ marginTop: spacing.sm }}>
                  <summary style={{ cursor: 'pointer', color: colors.blue }}>View Full Results</summary>
                  <pre style={{ 
                    marginTop: spacing.sm, 
                    padding: spacing.sm, 
                    backgroundColor: '#fff', 
                    borderRadius: layout.borderRadius,
                    overflow: 'auto',
                    fontSize: '0.75rem',
                    maxHeight: '400px'
                  }}>
                    {JSON.stringify(nwraTestResults, null, 2)}
                  </pre>
                </details>
              </>
            )}
          </div>
        )}
      </Card>
    );
  }

  function renderQueueSection() {
    return (
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>Pending Updates ({pendingUpdates.length})</span>
            <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center' }}>
              {lastRefreshTime && (
                <span style={{ fontSize: '0.75rem', color: colors.midGray }}>
                  Last refresh: {lastRefreshTime.toLocaleTimeString()}
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={handleManualRefresh}
                loading={isManuallyRefreshing || pendingUpdatesLoading}
                icon={<Icon name="refresh" type="action" size={16} />}
                aria-label="Refresh pending updates"
              >
                Refresh
              </Button>
            </div>
          </div>
        }
        variant="gradient"
        hoverable={false}
        style={{ marginBottom: spacing.lg }}
      >
        {pendingUpdatesLoading ? (
          <SkeletonLoader variant="card" count={3} />
        ) : pendingUpdates.length === 0 ? (
          <EmptyState
            icon="check"
            iconType="action"
            title="No pending updates"
            description="All JotForm submissions have been reviewed."
          />
        ) : (
          <>
            <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.md, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search by email or business name..."
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={styles.select}
                aria-label="Filter by status"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            {filteredPendingUpdates.length === 0 ? (
              <EmptyState
                icon="search"
                iconType="action"
                title="No results found"
                description={searchQuery ? `No pending updates match "${searchQuery}"` : `No ${filterStatus} updates found`}
              />
            ) : (
              <div style={{ fontSize: '0.875rem', color: colors.midGray, marginBottom: spacing.sm }}>
                Showing {filteredPendingUpdates.length} of {pendingUpdates.length} update(s)
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              {filteredPendingUpdates.map(update => (
                <div
                  key={update.id}
                  style={{
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: layout.borderRadius,
                    padding: spacing.md,
                    backgroundColor: colors.white,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: spacing.sm }}>
                    <div>
                      <div style={{ fontFamily: fonts.heading, fontWeight: 600, color: colors.darkGray, marginBottom: spacing.xs }}>
                        {update.client_email}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: colors.midGray }}>
                        Submitted: {new Date(update.created_at).toLocaleString()}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: update.user_id ? colors.green : colors.red, marginTop: spacing.xs }}>
                        {update.user_id ? 'User account found' : 'User account not found'}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() => {
                        setSelectedPendingUpdate(update);
                        setShowPendingModal(true);
                      }}
                    >
                      Review
                    </Button>
                  </div>
                  {update.mapped_data?.business_name && (
                    <div style={{ fontSize: '0.875rem', color: colors.midGray }}>
                      Business: {update.mapped_data.business_name}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    );
  }

  function renderClientsSection() {
    return (
      <div style={styles.layout} className="admin-split admin-layout">
        <div style={styles.pane} className="admin-pane">
          <Card title="Clients" variant="gradient" hoverable={false} style={{ padding: spacing.md }}>
            <div style={styles.paneScroll} className="admin-pane-scroll">
              {clientsLoading ? (
                <SkeletonLoader variant="table" count={5} />
              ) : clientsError ? (
                <p style={{ color: colors.error }}>{clientsError}</p>
              ) : activeClients.length === 0 ? (
                <EmptyState
                  icon="document"
                  iconType="document"
                  title="No clients found"
                  description="Clients will appear here once they create accounts."
                />
              ) : (
                <>
                  <div style={{ display: 'flex', gap: spacing.md, marginBottom: spacing.md, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '160px' }}>
                      <SearchBar
                        value={clientSearchQuery}
                        onChange={setClientSearchQuery}
                        placeholder="Search by name, email, or ID..."
                      />
                    </div>
                    <select
                      value={clientFilterEntityType}
                      onChange={(e) => setClientFilterEntityType(e.target.value)}
                      style={{ ...styles.select, width: 'auto', minWidth: '140px' }}
                      aria-label="Filter by entity type"
                    >
                      <option value="all">All entity types</option>
                      {uniqueEntityTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  {filteredClients.length === 0 ? (
                    <EmptyState
                      icon="search"
                      iconType="action"
                      title="No matching clients"
                      description="Try a different search or clear your filters."
                      actionLabel="Clear Filters"
                      onAction={clearClientFilters}
                    />
                  ) : (
                    <>
                      <div style={{ fontSize: '0.875rem', color: colors.midGray, marginBottom: spacing.sm }}>
                        Showing {filteredClients.length} of {activeClients.length} clients
                      </div>
                      <Table
                        columns={clientsTableColumns}
                        data={filteredClients}
                        onRowClick={(row) => selectClient(row)}
                        keyField="id"
                        selectedKey={selectedClient?.id}
                        responsive={false}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </Card>
        </div>

        <div style={styles.pane} className="admin-pane">
          <Card title="Client Detail" variant="gradient" hoverable={false}>
            <div style={styles.paneScroll} className="admin-pane-scroll">
              {!selectedClient ? (
                <EmptyState
                  icon="document"
                  iconType="document"
                  title="Select a client"
                  description="Click on a client from the list to view and manage their details."
                />
              ) : (
                renderClientDetail()
              )}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  function renderClientDetail() {
    return (
      <>
        <h3 style={{ fontFamily: fonts.heading, fontSize: '1.1rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.sm }}>
          {selectedClient.business_name || 'Incomplete profile'}
        </h3>
        <p style={{ color: colors.midGray, marginTop: 0, marginBottom: spacing.lg }}>
          Entity: {selectedClient.entity_type || '—'}
          <br />
          Email: {selectedClient.login_email || selectedClient.owner_email || selectedClient.business_email || '—'}
          <br />
          Portal login:{' '}
          {selectedClient.user_id ? (
            <span style={{ color: colors.green }}>Linked</span>
          ) : (
            <span style={{ color: colors.error }}>Not linked</span>
          )}
          <br />
          NWRA company:{' '}
          {selectedClient.nwra_company_id ? (
            <span style={{ fontFamily: 'monospace' }}>{selectedClient.nwra_company_id}</span>
          ) : (
            'Not linked'
          )}
        </p>

        <div style={{ 
          display: 'flex', 
          gap: spacing.sm, 
          borderBottom: `2px solid ${colors.gray200}`, 
          marginBottom: spacing.lg,
          flexWrap: 'wrap'
        }}>
          {[
            { id: 'information', label: 'Client Information' },
            { id: 'filings', label: 'Client Filings' },
            { id: 'integrations', label: 'NWRA & Stripe' },
            { id: 'serviceForm', label: 'Service Form' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveClientTab(tab.id)}
              style={{
                padding: `${spacing.sm} ${spacing.md}`,
                fontFamily: fonts.heading,
                fontSize: '0.9rem',
                fontWeight: activeClientTab === tab.id ? 600 : 400,
                color: activeClientTab === tab.id ? colors.blue : colors.midGray,
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeClientTab === tab.id ? `3px solid ${colors.blue}` : '3px solid transparent',
                cursor: 'pointer',
                marginBottom: '-2px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeClientTab === 'information' && (
          <div style={{ marginBottom: spacing.xl }}>
            <h4 style={{ fontFamily: fonts.heading, fontSize: '1rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.md }}>
              Edit Client Information
            </h4>
            <div style={{ marginBottom: spacing.lg }}>
              <h5 style={{ fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.sm, borderBottom: `1px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
                Business Profile
              </h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: spacing.md }}>
                <Input
                  label="Business name"
                  placeholder="Legal business name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  autoComplete="organization"
                />
                <EntityTypeSelect
                  value={entityType}
                  onChange={(e) => setEntityType(e.target.value)}
                />
                <Input
                  type="email"
                  label="Portal login email"
                  placeholder="client@example.com"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  helperText="The email this client uses to sign in. Save it, then use Link portal login if they already have an account."
                />
              </div>
              <div style={{ marginTop: spacing.md, display: 'flex', flexWrap: 'wrap', gap: spacing.sm, alignItems: 'center' }}>
                <Button variant="outline" onClick={linkPortalUser} loading={linkingPortalUser}>
                  {selectedClient.user_id ? 'Re-check / attach this email' : 'Link portal login'}
                </Button>
                <span style={{ fontSize: '0.85rem', color: colors.midGray }}>
                  {selectedClient.user_id
                    ? 'This company already has a portal account attached.'
                    : 'The person must already have signed up with this email.'}
                </span>
              </div>
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <h5 style={{ fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.sm, borderBottom: `1px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
                Owner Information
              </h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: spacing.md, marginBottom: spacing.md }}>
                <Input label="Owner Name" placeholder="Full name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
                <Input type="email" label="Owner Email" placeholder="email@example.com" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} />
                <Input type="tel" label="Owner Phone" placeholder="(555) 123-4567" value={ownerPhone} onChange={(e) => setOwnerPhone(e.target.value)} />
              </div>
              <Input label="Owner Address Line 1" placeholder="Street address" value={ownerAddressLine1} onChange={(e) => setOwnerAddressLine1(e.target.value)} style={{ marginBottom: spacing.sm }} />
              <Input label="Owner Address Line 2" placeholder="Apt, suite, etc. (optional)" value={ownerAddressLine2} onChange={(e) => setOwnerAddressLine2(e.target.value)} style={{ marginBottom: spacing.sm }} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: spacing.sm }}>
                <Input label="City" value={ownerCity} onChange={(e) => setOwnerCity(e.target.value)} />
                <Input label="State" value={ownerState} onChange={(e) => setOwnerState(e.target.value)} />
                <Input label="ZIP Code" value={ownerZip} onChange={(e) => setOwnerZip(e.target.value)} />
              </div>
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <h5 style={{ fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.sm, borderBottom: `1px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
                Business Information
              </h5>
              <Input label="Business Address Line 1" value={businessAddressLine1} onChange={(e) => setBusinessAddressLine1(e.target.value)} style={{ marginBottom: spacing.sm }} />
              <Input label="Business Address Line 2" value={businessAddressLine2} onChange={(e) => setBusinessAddressLine2(e.target.value)} style={{ marginBottom: spacing.sm }} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: spacing.sm, marginBottom: spacing.md }}>
                <Input label="City" value={businessCity} onChange={(e) => setBusinessCity(e.target.value)} />
                <Input label="State" value={businessState} onChange={(e) => setBusinessState(e.target.value)} />
                <Input label="ZIP Code" value={businessZip} onChange={(e) => setBusinessZip(e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: spacing.md }}>
                <Input type="tel" label="Business Phone" value={businessPhone} onChange={(e) => setBusinessPhone(e.target.value)} />
                <Input type="email" label="Business Email" value={businessEmail} onChange={(e) => setBusinessEmail(e.target.value)} />
                <Input label="EIN" value={ein} onChange={(e) => setEin(e.target.value)} />
                <Input label="Registration Number" value={registrationNumber} onChange={(e) => setRegistrationNumber(e.target.value)} />
              </div>
            </div>

            <div style={{ marginBottom: spacing.lg }}>
              <h5 style={{ fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.sm, borderBottom: `1px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
                Registered Agent Information
              </h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: spacing.md, marginBottom: spacing.md }}>
                <Input label="Registered Agent Name" value={registeredAgentName} onChange={(e) => setRegisteredAgentName(e.target.value)} />
                <Input type="tel" label="Registered Agent Phone" value={registeredAgentPhone} onChange={(e) => setRegisteredAgentPhone(e.target.value)} />
                <Input type="email" label="Registered Agent Email" value={registeredAgentEmail} onChange={(e) => setRegisteredAgentEmail(e.target.value)} />
              </div>
              <Input label="Registered Agent Address Line 1" value={registeredAgentAddressLine1} onChange={(e) => setRegisteredAgentAddressLine1(e.target.value)} style={{ marginBottom: spacing.sm }} />
              <Input label="Registered Agent Address Line 2" value={registeredAgentAddressLine2} onChange={(e) => setRegisteredAgentAddressLine2(e.target.value)} style={{ marginBottom: spacing.sm }} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: spacing.sm }}>
                <Input label="City" value={registeredAgentCity} onChange={(e) => setRegisteredAgentCity(e.target.value)} />
                <Input label="State" value={registeredAgentState} onChange={(e) => setRegisteredAgentState(e.target.value)} />
                <Input label="ZIP Code" value={registeredAgentZip} onChange={(e) => setRegisteredAgentZip(e.target.value)} />
              </div>
            </div>

            <Button onClick={saveClientInfo} loading={savingClient} variant="primary" style={{ marginTop: spacing.md }}>
              Save All Client Information
            </Button>

            <div style={{ marginTop: spacing.xl }}>
              <h5 style={{ fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.sm, borderBottom: `1px solid ${colors.gray200}`, paddingBottom: spacing.xs }}>
                Profile change history
              </h5>
              {profileChangesLoading ? (
                <p style={{ color: colors.midGray }}>Loading...</p>
              ) : profileChangesError ? (
                <p style={{ color: colors.midGray }}>{profileChangesError}</p>
              ) : !profileChanges || profileChanges.length === 0 ? (
                <p style={{ color: colors.midGray }}>No changes recorded yet.</p>
              ) : (
                <div style={{ overflowX: 'auto', maxHeight: '280px', overflowY: 'auto' }}>
                  <Table
                    columns={[
                      { key: 'changed_at', label: 'Date', render: (_, row) => new Date(row.changed_at).toLocaleString() },
                      { key: 'field_name', label: 'Field' },
                      { key: 'old_value', label: 'From', render: (val) => (val != null ? String(val).slice(0, 40) : '—') },
                      { key: 'new_value', label: 'To', render: (val) => (val != null ? String(val).slice(0, 40) : '—') },
                      { key: 'source', label: 'Source' },
                    ]}
                    data={profileChanges}
                    keyField="id"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {activeClientTab === 'integrations' && (
          <div>
            <h4 style={{ fontFamily: fonts.heading, fontSize: '1rem', fontWeight: 600, marginBottom: spacing.md }}>
              Link NWRA company
            </h4>
            <p style={{ color: colors.midGray, fontSize: '0.9rem', marginBottom: spacing.md }}>
              Search NWRA by company name, pick the correct match, then sync company fields and documents.
              This link is admin-only and is not shown in the client portal. Do not auto-pick the first result.
            </p>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.md }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <Input
                  label="Company name to search"
                  value={nwraSearchName}
                  onChange={(e) => setNwraSearchName(e.target.value)}
                />
              </div>
              <div style={{ alignSelf: 'flex-end' }}>
                <Button variant="outline" onClick={searchNWRACompanies} loading={nwraSearching}>
                  Find on NWRA
                </Button>
              </div>
            </div>
            {nwraSearchResults.length > 0 && (
              <div style={{ marginBottom: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                {nwraSearchResults.map((co) => {
                  const id = co.id || co.company_id;
                  const name = co.name || co.company_name || co.legal_name || String(id);
                  return (
                    <div
                      key={id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: spacing.md,
                        padding: spacing.md,
                        border: `1px solid ${colors.borderColor}`,
                        borderRadius: layout.borderRadius,
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{name}</div>
                        <div style={{ fontSize: '0.8rem', fontFamily: 'monospace', color: colors.midGray }}>{id}</div>
                        {(co.jurisdiction || co.state) && (
                          <div style={{ fontSize: '0.8rem', color: colors.midGray }}>
                            {co.jurisdiction || co.state} {co.entity_type ? `• ${co.entity_type}` : ''}
                          </div>
                        )}
                      </div>
                      <Button size="sm" variant="primary" onClick={() => linkNWRACompany(id, name)}>
                        Link this company
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            {selectedClient.nwra_company_id && (
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.xl }}>
                <Button variant="primary" onClick={syncNWRACompany} loading={syncingNWRACompany}>
                  Sync company info from NWRA
                </Button>
                <Button
                  variant="outline"
                  onClick={pullNWRADocuments}
                  loading={syncingNWRA}
                  disabled={!selectedClient.user_id}
                >
                  Pull documents from NWRA
                </Button>
              </div>
            )}

            <h4 style={{ fontFamily: fonts.heading, fontSize: '1rem', fontWeight: 600, marginBottom: spacing.md }}>
              Stripe customers
            </h4>
            <p style={{ color: colors.midGray, fontSize: '0.9rem', marginBottom: spacing.md }}>
              Pin Stripe customer IDs when email match is ambiguous. Billing reads from both the direct and JotForm Stripe accounts.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: spacing.md, marginBottom: spacing.md }}>
              <Input
                label="Stripe customer (direct account)"
                placeholder="cus_..."
                value={stripeCustomerDirect}
                onChange={(e) => setStripeCustomerDirect(e.target.value)}
              />
              <Input
                label="Stripe customer (JotForm account)"
                placeholder="cus_..."
                value={stripeCustomerJotform}
                onChange={(e) => setStripeCustomerJotform(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.md }}>
              <Button variant="primary" onClick={saveClientInfo} loading={savingClient}>
                Save Stripe IDs
              </Button>
              <Button variant="outline" onClick={loadClientBilling} loading={billingLoading}>
                Preview billing
              </Button>
            </div>
            {billingPreview && (
              <div style={{ padding: spacing.md, backgroundColor: colors.gray100, borderRadius: layout.borderRadius, fontSize: '0.875rem' }}>
                <div style={{ fontWeight: 600, marginBottom: spacing.sm }}>
                  {(billingPreview.payments || []).length} payment(s) · {(billingPreview.subscriptions || []).length} subscription(s)
                </div>
                <pre style={{ margin: 0, maxHeight: 240, overflow: 'auto', fontSize: '0.75rem' }}>
                  {JSON.stringify(billingPreview, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}

        {activeClientTab === 'filings' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap', marginBottom: spacing.md }}>
              <h4 style={{ fontFamily: fonts.heading, fontSize: '1rem', fontWeight: 600, color: colors.darkGray, margin: 0 }}>
                Client filings
              </h4>
              <Button
                variant="outline"
                size="sm"
                onClick={pullNWRADocuments}
                loading={syncingNWRA}
                disabled={syncingNWRA || !selectedClient?.user_id || !selectedClient?.nwra_company_id}
              >
                Pull from NWRA
              </Button>
            </div>
            {!selectedClient?.user_id && (
              <div style={{ padding: spacing.md, marginBottom: spacing.md, backgroundColor: '#fff5f5', border: `1px solid ${colors.error}`, borderRadius: layout.borderRadius, color: colors.error, fontSize: '0.9rem' }}>
                This client has no linked user account. Documents cannot be uploaded or synced until they create an account.
              </div>
            )}
            <div style={{ marginBottom: spacing.lg, opacity: selectedClient?.user_id ? 1 : 0.5, pointerEvents: selectedClient?.user_id ? 'auto' : 'none' }}>
              <Input label="Document title" placeholder="Document title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
              <div style={{ marginTop: spacing.md, marginBottom: spacing.md }}>
                <label style={{ display: 'block', fontFamily: fonts.heading, fontSize: '0.9rem', fontWeight: 500, marginBottom: spacing.xs, color: colors.darkGray }}>
                  Document type (optional)
                </label>
                <select value={documentType} onChange={(e) => setDocumentType(e.target.value)} style={styles.select}>
                  <option value="">Select document type</option>
                  {DOCUMENT_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: spacing.md, marginBottom: spacing.md }}>
                <Input label="Tag/Note (optional, max 30 chars)" value={tag} onChange={(e) => { if (e.target.value.length <= 30) setTag(e.target.value); }} />
                <Input label="Document date (optional)" type="date" value={customDate} onChange={(e) => setCustomDate(e.target.value)} />
              </div>
              <FileUploadZone key={uploadZoneKey} onFilesSelected={(selectedFiles) => setFiles(selectedFiles)} maxFiles={1} multiple={false} disabled={!selectedClient?.user_id} />
              <Button onClick={uploadForClient} loading={uploading} disabled={!selectedClient?.user_id || !newTitle || !files.length} variant="primary" fullWidth style={{ marginTop: spacing.md }}>
                Upload for client
              </Button>
            </div>
            {filingsLoading ? (
              <SkeletonLoader variant="card" count={3} />
            ) : filingsError ? (
              <p style={{ color: colors.error }}>{filingsError}</p>
            ) : clientFilings.length === 0 ? (
              <EmptyState icon="document" iconType="document" title="No filings for this client" description="Upload documents or pull from NWRA when linked." />
            ) : (
              <Table columns={filingsTableColumns} data={clientFilings} keyField="id" responsive />
            )}
          </div>
        )}

        {activeClientTab === 'serviceForm' && (
          <div>
            <h4 style={{ fontFamily: fonts.heading, fontSize: '1rem', fontWeight: 600, color: colors.darkGray, marginBottom: spacing.md }}>
              Business Initiative Service Form
            </h4>
            <div style={{ width: '100%', minHeight: '600px', border: `1px solid ${colors.borderColor}`, borderRadius: layout.borderRadius, overflow: 'hidden', backgroundColor: colors.white }}>
              <iframe
                id="JotFormIFrame-252943654936065"
                title="Business Initiative Service Form"
                allowTransparency="true"
                allow="geolocation; microphone; camera; fullscreen; payment"
                src="https://form.jotform.com/252943654936065"
                frameBorder="0"
                style={{ minWidth: '100%', maxWidth: '100%', height: '600px', border: 'none' }}
                scrolling="no"
              />
            </div>
          </div>
        )}
      </>
    );
  }
}

export default Admin;
